import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Users, Clock, Euro, Shirt } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import BookingModal from '../../components/BookingModal';

const EleganceNordique = () => {
  const { language } = useLanguage();
  const [showBookingModal, setShowBookingModal] = useState(false);

  const eventData = {
    id: '662e8a88-efa6-470d-8427-944ad5a0bb35',
    title: 'Soirée de Gala H&G : Élégance Nordique à Paris',
    title_en: 'H&G Gala Evening: Nordic Elegance in Paris',
    date: '5 Mars 2026',
    location: 'Paris, France',
    price_member: 0,
    price_non_member: 1290,
    total_seats: 80,
    seats_sold: 0
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        height: '500px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0
        }}>
          <img
            src="https://customer-assets.emergentagent.com/job_global-advisory-1/artifacts/76iilsfr_-agathe-palsvig.jpg"
            alt="Élégance Nordique"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(255, 254, 242, 0.3), rgba(255, 254, 242, 0.9))'
          }} />
        </div>

        <div className="container" style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center'
        }}>
          <Link to="/member/events" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '24px',
            color: 'var(--text-primary)',
            textDecoration: 'none',
            fontSize: '14px',
            padding: '8px 16px',
            background: 'rgba(255, 254, 242, 0.9)',
            border: '1px solid var(--border-light)'
          }}>
            <ArrowLeft size={16} />
            {language === 'fr' ? 'Retour aux événements' : 'Back to events'}
          </Link>
          
          <h1 className="hero-large" style={{ marginBottom: '16px' }}>
            {language === 'fr' ? 'Gala Privé Henry & Gaby' : 'Henry & Gaby Private Gala'}
          </h1>
          <h2 className="hero-medium" style={{ 
            marginBottom: '24px',
            fontStyle: 'italic',
            color: 'var(--text-secondary)'
          }}>
            {language === 'fr' ? 'Élégance Nordique à Paris' : 'Nordic Elegance in Paris'}
          </h2>
          
          <div style={{
            display: 'flex',
            gap: '24px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={20} />
              <span className="body-large">5 Mars 2026</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MapPin size={20} />
              <span className="body-large">Champ House Paris</span>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <p className="body-large" style={{
            lineHeight: 1.8,
            textAlign: 'center',
            marginBottom: '32px'
          }}>
            {language === 'fr'
              ? "Plongez dans l'univers raffiné et élégant des soirées Henry & Gaby, où chaque événement est une célébration de l'art de vivre exclusif."
              : "Immerse yourself in the refined and elegant world of Henry & Gaby evenings, where each event is a celebration of exclusive lifestyle."}
          </p>
          <p className="body-large" style={{
            lineHeight: 1.8,
            color: 'var(--text-secondary)',
            textAlign: 'center'
          }}>
            {language === 'fr'
              ? "Le 5 mars 2026, le Champ House Paris accueillera le Club Privé Henry & Gaby pour une soirée prestigieuse aux accents scandinaves. Conçue comme un pont entre cultures d'excellence, elle mettra en résonance l'art, le design, la musique, la mode et le cinéma nordique avec le raffinement parisien et son élégance à la française, créant une harmonie unique."
              : "On March 5, 2026, Champ House Paris will host the Henry & Gaby Private Club for a prestigious evening with Scandinavian accents. Designed as a bridge between cultures of excellence, it will bring together Nordic art, design, music, fashion and cinema with Parisian refinement and French elegance, creating a unique harmony."}
          </p>
        </div>
      </section>

      {/* Programme Horaire */}
      <section className="section-padding">
        <div className="container" style={{ maxWidth: '1000px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '48px', textAlign: 'center' }}>
            {language === 'fr' ? 'Programme de la Soirée' : 'Evening Program'}
          </h2>
          
          <div style={{
            display: 'grid',
            gap: '0',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)'
          }}>
            {[
              { 
                time: "19h30", 
                title: language === 'fr' ? "Accueil des invités" : "Guest reception", 
                description: language === 'fr' ? "Réception et vestiaire" : "Reception and cloakroom" 
              },
              { 
                time: "20h00", 
                title: language === 'fr' ? "Cocktail d'accueil" : "Welcome cocktail", 
                description: language === 'fr' ? "Champagne et canapés sur les rooftops avec vue sur les Champs-Élysées" : "Champagne and canapés on the rooftops with view of the Champs-Élysées" 
              },
              { 
                time: "20h45", 
                title: language === 'fr' ? "Exposition et découvertes" : "Exhibition and discoveries", 
                description: language === 'fr' ? "Visite libre des expositions Ole Mathiesen et Tove Bowyer" : "Free visit of Ole Mathiesen and Tove Bowyer exhibitions" 
              },
              { 
                time: "21h15", 
                title: language === 'fr' ? "Dîner gastronomique" : "Gourmet dinner", 
                description: language === 'fr' ? "Buffet élégant fusion franco-scandinave avec dégustation de caviar" : "Elegant Franco-Scandinavian fusion buffet with caviar tasting" 
              },
              { 
                time: "22h30", 
                title: language === 'fr' ? "Concert Jazz Live" : "Live Jazz Concert", 
                description: language === 'fr' ? "Performance de Björn Ingelstam" : "Performance by Björn Ingelstam" 
              },
              { 
                time: "23h15", 
                title: language === 'fr' ? "Présentation Mode" : "Fashion presentation", 
                description: language === 'fr' ? "Défilé Rotate Birger Christensen" : "Rotate Birger Christensen fashion show" 
              },
              { 
                time: "23h45", 
                title: language === 'fr' ? "Rencontre cinéma" : "Cinema encounter", 
                description: language === 'fr' ? "Échange avec Agathe Levi, lauréate Stockholm City Film Festival" : "Exchange with Agathe Levi, Stockholm City Film Festival award winner" 
              },
              { 
                time: "00h30", 
                title: language === 'fr' ? "Fin de soirée" : "End of evening", 
                description: language === 'fr' ? "Cigares premium et derniers échanges sur les rooftops" : "Premium cigars and final exchanges on the rooftops" 
              }
            ].map((item, index) => (
              <div key={index} style={{
                padding: '24px 32px',
                borderBottom: index < 7 ? '1px solid var(--border-light)' : 'none',
                display: 'flex',
                gap: '32px',
                alignItems: 'flex-start'
              }}>
                <div style={{
                  minWidth: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Clock size={18} color="var(--text-secondary)" />
                  <span className="body-large" style={{ fontWeight: 600 }}>
                    {item.time}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <h3 className="heading-2" style={{ marginBottom: '4px' }}>
                    {item.title}
                  </h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Artistes et Intervenants */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '48px', textAlign: 'center' }}>
            {language === 'fr' ? 'Artistes et Intervenants' : 'Artists and Guests'}
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                title: "Ole Mathiesen",
                subtitle: language === 'fr' ? "Horlogerie Danoise" : "Danish Watchmaking",
                description: language === 'fr' 
                  ? "Horlogerie emblématique depuis 1919, symbole du design scandinave. Montres conçues pour durer un siècle, esthétique épurée et savoir-faire horloger suisse."
                  : "Iconic watchmaking since 1919, symbol of Scandinavian design. Watches designed to last a century, refined aesthetics and Swiss watchmaking expertise."
              },
              {
                title: "Tove Bowyer",
                subtitle: language === 'fr' ? "Artiste Danoise" : "Danish Artist",
                description: language === 'fr' 
                  ? "Sculptures en albâtre, bronze et bois, peintures poétiques inspirées de la nature nordique. Un univers minimaliste et expressif."
                  : "Sculptures in alabaster, bronze and wood, poetic paintings inspired by Nordic nature. A minimalist and expressive universe."
              },
              {
                title: "Björn Ingelstam",
                subtitle: language === 'fr' ? "Trompettiste & Chanteur Suédois" : "Swedish Trumpeter & Singer",
                description: language === 'fr' 
                  ? "Reconnu sur la scène jazz européenne. Fusion élégante de jazz classique, swing et blues."
                  : "Recognized on the European jazz scene. Elegant fusion of classic jazz, swing and blues."
              },
              {
                title: "Rotate Birger Christensen",
                subtitle: language === 'fr' ? "Maison de Haute Couture" : "Haute Couture House",
                description: language === 'fr' 
                  ? "Maison danoise célébrant l'élégance et le glamour scandinave. Silhouettes sculpturales, sequins, plumes et coupes spectaculaires."
                  : "Danish house celebrating Scandinavian elegance and glamour. Sculptural silhouettes, sequins, feathers and spectacular cuts."
              },
              {
                title: "Agathe Levi",
                subtitle: language === 'fr' ? "Actrice - Lauréate Stockholm 2025" : "Actress - Stockholm 2025 Award Winner",
                description: language === 'fr' 
                  ? "Lauréate du prix de Meilleure Actrice au Stockholm City Film Festival. Figure montante du cinéma nordique."
                  : "Best Actress award winner at Stockholm City Film Festival. Rising figure in Nordic cinema."
              }
            ].map((item, index) => (
              <div key={index} style={{
                padding: '28px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-light)',
                textAlign: 'center'
              }}>
                <h3 className="heading-2" style={{ marginBottom: '8px' }}>
                  {item.title}
                </h3>
                <p className="body-regular" style={{
                  color: 'var(--text-primary)',
                  fontWeight: 600,
                  marginBottom: '16px',
                  fontSize: '14px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {item.subtitle}
                </p>
                <p className="body-regular" style={{
                  color: 'var(--text-secondary)',
                  lineHeight: 1.7,
                  fontSize: '15px'
                }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gastronomie */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '24px', textAlign: 'center' }}>
            Expérience Gastronomique Raffinée
          </h2>
          <p className="body-large" style={{
            lineHeight: 1.8,
            color: 'var(--text-secondary)',
            textAlign: 'center',
            marginBottom: '48px'
          }}>
            Une fusion franco-scandinave préparée par des chefs talentueux
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px'
          }}>
            {[
              "Buffet élégant assis et debout",
              "Champagne et vins sélectionnés du Château",
              "Dégustation de caviar exclusif",
              "Spiritueux de qualité",
              "Cocktails raffinés",
              "Cigares premium"
            ].map((item, index) => (
              <div key={index} style={{
                padding: '24px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-light)',
                textAlign: 'center'
              }}>
                <p className="body-regular">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Le Champ House */}
      <section className="section-padding">
        <div className="container" style={{ maxWidth: '1000px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '24px', textAlign: 'center' }}>
            Le Champ House Paris
          </h2>
          <p className="body-large" style={{
            lineHeight: 1.8,
            color: 'var(--text-secondary)',
            textAlign: 'center',
            marginBottom: '32px'
          }}>
            Un joyau architectural au cœur des Champs-Élysées
          </p>
          
          <div style={{
            padding: '40px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-light)'
          }}>
            <p className="body-regular" style={{
              lineHeight: 1.8,
              color: 'var(--text-secondary)',
              marginBottom: '24px'
            }}>
              {language === 'fr'
                ? "Situé au cœur des Champs-Élysées, le Champ House incarne l'élégance et le raffinement parisien. Ce lieu exclusif conjugue architecture haussmannienne et modernité subtile : boiseries sculptées, cheminées imposantes, moulures, miroirs anciens et plafonds hauts créant une atmosphère majestueuse et intimiste."
                : "Located in the heart of the Champs-Élysées, Champ House embodies Parisian elegance and refinement. This exclusive venue combines Haussmannian architecture with subtle modernity: carved woodwork, imposing fireplaces, moldings, antique mirrors and high ceilings creating a majestic and intimate atmosphere."}
            </p>
            <p className="body-regular" style={{
              lineHeight: 1.8,
              color: 'var(--text-secondary)'
            }}>
              <strong>{language === 'fr' ? 'Deux magnifiques rooftops' : 'Two magnificent rooftops'}</strong> {language === 'fr' 
                ? "offrant l'une des plus belles vues sur les Champs-Élysées de Paris, cadre idéal pour savourer champagne ou déguster un cigare."
                : "offering one of the most beautiful views of the Champs-Élysées in Paris, the ideal setting to enjoy champagne or savor a cigar."}
            </p>
          </div>
        </div>
      </section>

      {/* Informations Pratiques */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '48px', textAlign: 'center' }}>
            {language === 'fr' ? 'Informations Pratiques' : 'Practical Information'}
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px',
            marginBottom: '48px'
          }}>
            <div style={{
              padding: '32px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
                border: '1px solid var(--border-light)',
                background: 'var(--bg-secondary)'
              }}>
                <Calendar size={24} />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '8px' }}>
                {language === 'fr' ? 'Date' : 'Date'}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {language === 'fr' ? '5 Mars 2026' : 'March 5, 2026'}
              </p>
            </div>

            <div style={{
              padding: '32px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
                border: '1px solid var(--border-light)',
                background: 'var(--bg-secondary)'
              }}>
                <MapPin size={24} />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '8px' }}>
                {language === 'fr' ? 'Lieu' : 'Location'}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Champ House Paris<br />
                Champs-Élysées, Paris
              </p>
            </div>

            <div style={{
              padding: '32px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
                border: '1px solid var(--border-light)',
                background: 'var(--bg-secondary)'
              }}>
                <Users size={24} />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '8px' }}>Exclusivité</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Réservé aux membres du club et leurs compagnons
              </p>
            </div>
          </div>

          {/* Dress Code */}
          <div style={{
            padding: '32px',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)',
            textAlign: 'center'
          }}>
            <Shirt size={32} style={{ margin: '0 auto 16px' }} />
            <h3 className="heading-2" style={{ marginBottom: '16px' }}>Dress Code</h3>
            <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
              {language === 'fr' ? (
                <>
                  <strong>Tenue élégante exigée</strong><br />
                  Hommes : Cravate ou nœud papillon demandé<br />
                  Femmes : Robe de soirée ou ensemble élégant
                </>
              ) : (
                <>
                  <strong>Elegant attire required</strong><br />
                  Men: Tie or bow tie required<br />
                  Women: Evening gown or elegant outfit
                </>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-small">
        <div className="container" style={{ maxWidth: '600px', textAlign: 'center' }}>
          <p className="body-large" style={{ marginBottom: '24px', lineHeight: 1.7 }}>
            {language === 'fr' ? 'Réservez votre place pour cet événement exclusif' : 'Book your place for this exclusive event'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <button onClick={() => setShowBookingModal(true)} className="btn-primary">
              {language === 'fr' ? 'RÉSERVER' : 'BOOK NOW'}
            </button>
            <Link to="/member/events" className="btn-secondary">
              {language === 'fr' ? 'Retour aux événements' : 'Back to events'}
            </Link>
          </div>
        </div>
      </section>

      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        item={eventData}
        bookingType="event"
        onSuccess={() => setShowBookingModal(false)}
      />
    </div>
  );
};

export default EleganceNordique;
